import React, { useState } from 'react';
import {
  Box,
  IconButton,
  VStack,
  Text,
  Button,
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Image as ChakraImage,
  useBreakpointValue,
  useToast,
  AspectRatio,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  ArrowsClockwise,
  Image,
  Star
} from '@phosphor-icons/react';

export default function SiteImageManager({ 
  site,
  media,
  mainImage,
  canEdit,
  onUpdateMainImage
}) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectingMainImage, setSelectingMainImage] = useState(false);
  const toast = useToast();

  const handleMediaClick = (url) => {
    setSelectedImage(url);
  };

  const handleMainImageClick = () => {
    const mediaItem = media.find(m => m.url === mainImage);
    if (mediaItem) {
      setSelectedImage(mediaItem.url);
    }
  };

  const handleSetMainImage = async (mediaUrl) => {
    try {
      await onUpdateMainImage(mediaUrl);
      toast({
        title: 'Success',
        description: 'Main image updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSelectMainImage = async (mediaItem) => {
    await handleSetMainImage(mediaItem.url);
    setSelectingMainImage(false);
  };

  return (
    <>
      {/* Main Image Display */}
      {mainImage ? (
        <Box
          position="relative"
          cursor={canEdit ? "default" : "pointer"}
          onClick={canEdit ? undefined : handleMainImageClick}
          transition="transform 0.2s"
          _hover={{ transform: canEdit ? "none" : "scale(1.02)" }}
          borderRadius="md"  // Changed from lg to md
          overflow="hidden"
          boxShadow="md"  // Changed from lg to md
          height="100%"
          maxHeight="300px"
          borderWidth="1px"
          borderColor="border.default"  // Add border
        >
          <AspectRatio ratio={1} height="100%">
            <ChakraImage
              src={mainImage}
              alt={site.name}
              objectFit="cover"
              w="100%"
              h="100%"
              borderRadius="lg"
            />
          </AspectRatio>
          
          {canEdit && (
            <Box
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              bg="blackAlpha.700"
              p={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              borderTopWidth="1px"
              borderColor="border.default"
              zIndex={2}  // Ensure controls are above the texture
            >
              <Heading
                color="white"
                size="sm"
                fontFamily="heading"  // Use Lora
                letterSpacing="tight"
              >
                Main Site Image
              </Heading>
              <IconButton
                icon={<ArrowsClockwise size={16} />}
                size="sm"
                variant="artifact"  // Use artifact variant
                onClick={() => setSelectingMainImage(true)}
                aria-label="Change main image"
              />
            </Box>
          )}
        </Box>
      ) : (
        <Box
          height="100%"
          minHeight="300px"
          borderRadius="md"  // Changed from lg to md
          bg="bg.surface"  // Use semantic token
          display="flex"
          alignItems="center"
          justifyContent="center"
          color="text.muted"  // Use semantic token
          boxShadow="md"
          borderWidth="1px"
          borderColor="border.default"  // Add border
          position="relative"
        >
          {/* Add subtle texture */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            opacity={0.1}
            bgImage="var(--texture-parchment)"
            bgRepeat="repeat"
            bgSize="200px"
            bgBlendMode="multiply"
            zIndex={0}
            pointerEvents="none"
          />
          
          <VStack spacing={2} position="relative" zIndex={1}>
            <Image size={48} weight="thin" />
            <Text fontFamily="body" color="text.muted">
              No main image selected
            </Text>
            {canEdit && (
              <Button
                size="sm"
                variant="journal"  // Use journal variant
                leftIcon={<Image size={16} />}
                onClick={() => setSelectingMainImage(true)}
              >
                Select Image
              </Button>
            )}
          </VStack>
        </Box>
      )}

      {/* Image Preview Modal */}
      <Modal 
        isOpen={!!selectedImage} 
        onClose={() => setSelectedImage(null)} 
        size="4xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody p={0}>
            <Box position="relative">
              {selectedImage && (
                <ChakraImage
                  src={selectedImage}
                  alt="Selected site image"
                  w="full"
                  h="auto"
                  maxH="90vh"
                  objectFit="contain"
                />
              )}
              {canEdit && selectedImage && (
                <Button
                  leftIcon={<Star weight={selectedImage === mainImage ? "fill" : "regular"} />}
                  position="absolute"
                  top={4}
                  right={4}
                  variant="journal"
                  colorScheme="brand"
                  onClick={async (e) => {
                    e.stopPropagation();
                    await handleSetMainImage(selectedImage);
                    setSelectedImage(null);
                  }}
                >
                  {selectedImage === mainImage ? "Current Main Image" : "Set as Main Image"}
                </Button>
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Media Selection Modal */}
      {selectingMainImage && (
        <Box mt={6}>
          <Heading
            size="md"
            mb={4}
            fontFamily="heading"
            color="text.default"
            letterSpacing="tight"
          >
            Select Main Site Image
          </Heading>
          <Text mb={4} color="text.muted" fontFamily="body">
            Choose an image to be the main representative image for this site. This image will be displayed prominently on the site profile and in search results.
          </Text>
          
          <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing={4}>
            {media && media.map((item) => (
              <Box 
                key={item.id}
                borderRadius="md"
                overflow="hidden"
                boxShadow={item.url === mainImage ? "0 0 0 3px var(--chakra-colors-brand-primary)" : "md"}
                borderWidth="1px"
                borderColor={item.url === mainImage ? "brand.primary" : "border.default"}
                cursor="pointer"
                onClick={() => handleSelectMainImage(item)}
                transition="transform 0.2s"
                _hover={{ transform: 'scale(1.05)' }}
                position="relative"
              >
                <AspectRatio ratio={1}>
                  <ChakraImage
                    src={item.url}
                    alt={item.caption || 'Site image'}
                    objectFit="cover"
                  />
                </AspectRatio>
                
                {item.url === mainImage && (
                  <Box
                    position="absolute"
                    top={0}
                    right={0}
                    bg="brand.primary"
                    color="text.inverse"
                    p={1}
                    borderBottomLeftRadius="md"
                  >
                    <Star weight="fill" size={16} />
                  </Box>
                )}
              </Box>
            ))}
          </SimpleGrid>
          
          <Button
            mt={4}
            variant="secondary"  // Use secondary variant
            onClick={() => setSelectingMainImage(false)}
          >
            Cancel Selection
          </Button>
        </Box>
      )}
    </>
  );
}
